import { useState, useCallback, useEffect, useRef, useMemo } from 'react';

import { PreviewSessionStatus, PreviewSessionEntry, PreviewSessionInput } from '../types';

import { usePreviewSessionStorage } from './usePreviewSessionStorage';

export interface UsePreviewSessionManagerOptions {
  marketplace: string;
  deviceFamily: string;
  placement: string;
}

export interface UsePreviewSessionManagerReturn {
  sessions: PreviewSessionEntry[];
  addSession: (session: PreviewSessionInput) => void;
  removeSession: (deviceId: string) => void;
  updateSessionStatus: (deviceId: string, status: PreviewSessionStatus) => void;
  removeExpiredSessions: () => void;
  getSessionsForCustomer: (customerId: string) => PreviewSessionEntry[];
  getOtherSessionForCustomerByDevice: (deviceId: string) => PreviewSessionEntry[];
  getSession: (deviceId: string) => PreviewSessionEntry | undefined;
}

const EXPIRY_CHECK_INTERVAL_MS = 600_000;

/**
 * UI state layer for preview sessions.
 * Consumes the storage hook (db) and adds ephemeral TERMINATING status.
 * On refresh, sessions hydrate as ACTIVE — user can re-trigger idempotent terminate.
 */
export const usePreviewSessionManager = (
  options: UsePreviewSessionManagerOptions,
): UsePreviewSessionManagerReturn => {
  const { marketplace, deviceFamily, placement } = options;
  const { getPersistedSessions, persistSession, deleteSession, deleteExpiredSessions } =
    usePreviewSessionStorage();

  // ─── State ─────────────────────────────────────────────────────────────────

  /** In-memory UI state: includes TERMINATING for button spinners */
  const [sessions, setSessions] = useState<PreviewSessionEntry[]>(() =>
    getPersistedSessions().map((s) => ({ ...s, status: PreviewSessionStatus.ACTIVE })),
  );

  // ─── Derived State ─────────────────────────────────────────────────────────

  const sessionMap = useMemo(() => new Map(sessions.map((s) => [s.deviceId, s])), [sessions]);
  const sessionMapRef = useRef(sessionMap);
  sessionMapRef.current = sessionMap;

  // ─── Mutations ─────────────────────────────────────────────────────────────

  /** Adds a new session to the current context and persists to storage */
  const addSession = useCallback(
    (session: PreviewSessionInput) => {
      const persistedSession = { ...session, marketplace, deviceFamily, placement };
      persistSession(persistedSession);
      setSessions((prev) => [
        ...prev.filter((s) => s.deviceId !== session.deviceId),
        { ...persistedSession, status: PreviewSessionStatus.ACTIVE },
      ]);
    },
    [persistSession, marketplace, deviceFamily, placement],
  );

  /** Removes a session from in-memory state and storage */
  const removeSession = useCallback(
    (deviceId: string) => {
      deleteSession(deviceId);
      setSessions((prev) => {
        if (!prev.some((s) => s.deviceId === deviceId)) return prev;
        return prev.filter((s) => s.deviceId !== deviceId);
      });
    },
    [deleteSession],
  );

  /**
   * Updates session status in-memory only.
   * TERMINATING is ephemeral (button spinner) — not persisted.
   */
  const updateSessionStatus = useCallback((deviceId: string, status: PreviewSessionStatus) => {
    setSessions((prev) => {
      if (!prev.some((s) => s.deviceId === deviceId)) return prev;
      return prev.map((s) => (s.deviceId === deviceId ? { ...s, status } : s));
    });
  }, []);

  /** Removes expired sessions from both in-memory state and storage */
  const removeExpiredSessions = useCallback(() => {
    deleteExpiredSessions();
    setSessions((prev) => {
      const now = Date.now();
      const nonExpiredSessions = prev.filter((s) => s.expiresAt > now);
      return nonExpiredSessions.length === prev.length ? prev : nonExpiredSessions;
    });
  }, [deleteExpiredSessions]);

  // ─── Queries ───────────────────────────────────────────────────────────────

  /** Returns all sessions for a given customerId */
  const getSessionsForCustomer = useCallback((customerId: string): PreviewSessionEntry[] => {
    return Array.from(sessionMapRef.current.values()).filter((s) => s.customerId === customerId);
  }, []);

  /** Returns other sessions sharing the same customerId as the given device */
  const getOtherSessionForCustomerByDevice = useCallback(
    (deviceId: string): PreviewSessionEntry[] => {
      const session = sessionMapRef.current.get(deviceId);
      if (!session || !session.customerId) return [];
      return Array.from(sessionMapRef.current.values()).filter(
        (s) => s.customerId === session.customerId && s.deviceId !== deviceId,
      );
    },
    [],
  );

  /** Returns a session by deviceId, or undefined if not found */
  const getSession = useCallback((deviceId: string): PreviewSessionEntry | undefined => {
    return sessionMapRef.current.get(deviceId);
  }, []);

  // ─── Effects ───────────────────────────────────────────────────────────────

  /** Auto-expiry: remove expired sessions every 10 minutes */
  const hasActiveSessions = sessions.length > 0;
  useEffect(() => {
    if (!hasActiveSessions) return;
    const timer = setInterval(() => {
      const now = Date.now();
      setSessions((prev) => {
        const nonExpiredSessions = prev.filter((s) => s.expiresAt > now);
        if (nonExpiredSessions.length === prev.length) return prev;
        return nonExpiredSessions;
      });
      deleteExpiredSessions();
    }, EXPIRY_CHECK_INTERVAL_MS);
    return () => clearInterval(timer);
  }, [hasActiveSessions, deleteExpiredSessions]);

  // ─── Return ────────────────────────────────────────────────────────────────

  return useMemo(
    () => ({
      sessions,
      addSession,
      removeSession,
      updateSessionStatus,
      removeExpiredSessions,
      getSessionsForCustomer,
      getOtherSessionForCustomerByDevice,
      getSession,
    }),
    [
      sessions,
      addSession,
      removeSession,
      updateSessionStatus,
      removeExpiredSessions,
      getSessionsForCustomer,
      getOtherSessionForCustomerByDevice,
      getSession,
    ],
  );
};
